import { MondayErrorCodes, MondayItemError, MondayItemData } from "shared/integrations/monday-types";

/**
 * This file is about sending to backend a batch of GET requests instead of sending them one at a time.
 *
 * The main component is a class that accepts ids to fetch and will debounce and send them together.
 *
 * The actual sending is done by calling an outside function, given to the constructor.
 * That function takes a list of keys and should return object with error/value for each key.
 */

interface FetchRequest {
  id: string;
  resolve: (item: MondayItemData | MondayItemError) => void;
  reject: (reason: Error) => void;
}

function isMondayItemError(object: unknown): object is MondayItemError {
  return (
    !!object &&
    typeof object === "object" &&
    !Array.isArray(object) &&
    typeof (object as MondayItemError).id === "string" &&
    typeof (object as MondayItemError).error === "string" &&
    Object.values(MondayErrorCodes).includes((object as MondayItemError).error)
  );
}

type Function1<Input, Output = unknown> = (input: Input) => Output;

const combineBoth =
  <T>(a: Function1<T>, b: Function1<T>) =>
  (x: T) => {
    a(x);
    b(x);
  };

export class DebounceFetchRequests {
  private pendingRequests = new Map<string, FetchRequest>();
  private fetching: ReturnType<typeof window.setTimeout> | null = null;

  constructor(
    readonly makeRequestToBackend: (data: string[]) => Promise<Record<string, MondayItemData | MondayItemError>>,
    readonly debounceMs = 100
  ) {}

  fetch(id: string) {
    if (!id) return Promise.resolve();

    if (this.pendingRequests.has(id)) {
      return new Promise((resolve, reject) => {
        const existing = this.pendingRequests.get(id);
        if (!existing) return;
        const { resolve: originalResolve, reject: originalReject } = existing;
        existing.resolve = combineBoth(resolve, originalResolve);
        existing.reject = combineBoth(reject, originalReject);
      });
    }

    return new Promise((resolve, reject) => {
      // Add to pending batch
      this.pendingRequests.set(id, {
        id,
        resolve,
        reject,
      });
      this.scheduleFetch();
    });
  }

  private scheduleFetch() {
    if (!this.fetching) {
      this.fetching = setTimeout(() => {
        this.processBatch();
      }, this.debounceMs);
    }
  }

  private async processBatch() {
    const pending = this.pendingRequests;

    // reset the state so clients can queue up more fetches
    this.pendingRequests = new Map();
    this.fetching = null;

    const keys = [...pending.values()].map(({ id }) => id);

    this.makeRequestToBackend(keys)
      .then((response) => {
        // Response is {[id]: MondayItemError | MondayItemData}
        // Every id we sent should be present in the response
        for (const [id, errorOrValue] of Object.entries(response)) {
          const listeners = pending.get(id);
          if (!listeners) {
            console.error("Backend returned key that's not in original input request");
            continue;
          }
          // Check for MondayItemError (contains error code)
          if (isMondayItemError(errorOrValue)) {
            const error = new Error(errorOrValue.error) as Error & { code: MondayErrorCodes; url?: string };
            error.code = errorOrValue.error;
            if (errorOrValue.url) {
              error.url = errorOrValue.url;
            }
            listeners.reject(error);
          } else {
            listeners.resolve(errorOrValue);
          }
        }
      })
      .catch((error) => {
        for (const { reject } of pending.values()) reject(error);
      });
  }
}
